<template>
  <el-select
    v-bind="$attrs"
    :model-value="modelValue"
    style="width: 200px"
    filterable
    remote
    ref="sref"
    :clearable="props.clearable"
    :placeholder="placeholder"
    :loading="remoteLoading"
    :remote-method="props.srcDataType === 'sys_user' ? remoteMethod : remoteTeacherMethod"
    @update:modelValue="updateVal"
    @change="changeVal"
    :multiple="props.multiple"
  >
    <el-option v-for="s in options" :key="s.userId" :label="s[label]" :value="s[value]" />
  </el-select>
</template>

<script setup>
/*
 * 员工、老师表远程搜索器--封装
 *
 * 大多情况，老师也是员工，故员工即能搜出来老师
 *
 * 少数情况，若取的师资信息表，需带上属性：src-data-type="szxx"
 */
import { ref } from 'vue';
import { findStaffQuery } from '@/api/morningRead/report';
import { listTeacher } from '@/api/education/teacher';

const props = defineProps({
  modelValue: {
    // 直接使用v-model，此组件已实现双向绑定
    type: [String, Array, Number],
    default: undefined,
  },
  placeholder: {
    type: String,
    default: '请输入姓名/完整工号',
  },
  label: {
    type: String,
    default: 'showName',
  },
  value: {
    type: String,
    default: 'userId',
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  srcDataType: {
    // 数据来源表，会影响从哪一张表查数据
    type: String,
    validator: function (value) {
      return ['sys_user', 'szxx'].includes(value); // 用户表、师资信息表。
    },
    default: 'sys_user',
  },
  status: {
    // 员工查询 0:启用，1，禁用，2:全部 默认为不传参 接口默认处理为0:启用
    type: Number,
  },
  enable: {
    type: String, // 师资 1启用 0禁用
    default: '',
  },
  multipleList: {
    type: Array, //多选传入 默认选项，回显
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: false,
  },
});
const sref = ref();
const options = ref([]);
const remoteLoading = ref(false);
const remoteMethod = (query) => {
  if (query) {
    remoteLoading.value = true;
    findStaffQuery(query, undefined, props.status)
      .then((res) => {
        remoteLoading.value = false;
        if (res.code === 200) {
          options.value = (res.data.rows || []).map((m) => ({
            ...m,
            showName: `${m.name}` + (m.employeeNo ? `(${m.employeeNo})` : ''),
          }));
        }
      })
      .catch(() => {
        remoteLoading.value = false;
        options.value = [];
      });
  } else {
    options.value = [];
  }
};

// 师资信息搜索
const remoteTeacherMethod = (query) => {
  if (query) {
    remoteLoading.value = true;
    let pa;
    const isNum = /\d/.test(query);
    if (isNum) {
      // 如果包含数字，则传工号，否则为姓名
      pa = {
        employeeId: query,
        pageSize: 2000,
        pageNum: 1,
        enable: props.enable,
      };
    } else {
      pa = {
        name: query,
        pageSize: 2000,
        pageNum: 1,
        enable: props.enable,
      };
    }
    listTeacher(pa)
      .then((res) => {
        remoteLoading.value = false;
        if (res.code === 200) {
          options.value = (res.rows || []).map((m) => ({
            ...m,
            showName: `${m.name}` + (m.employeeId ? `(${m.employeeId})` : ''),
            userId: m.id, // 师资表id
          }));
        }
      })
      .catch(() => {
        remoteLoading.value = false;
        options.value = [];
      });
  } else {
    options.value = [];
  }
};

const emit = defineEmits(['update:modelValue', 'change','user-list']);

function updateVal(val) {
  if(Array.isArray(val)){
    emit('user-list', options.value.filter((f) => val.includes(f[props.value])) || []); // change事件抛出选中的对象信息
  }
  emit('update:modelValue', val);
}
function getInnerRef() {
  return sref.value;
}
defineExpose({
  // 回显时调用，传入老师姓名或完整工号，手动进行一次远程查询
  echoData: props.srcDataType === 'sys_user' ? remoteMethod : remoteTeacherMethod,

  echoByTeacherId, // 师资信息时，传入老师的id进行一次远程查询(适用于回显时无工号的情况)
  echoByStaffId, // 用户表时，传入员工的id进行一次远程查询(适用于回显时无工号的情况)
  getInnerRef,
});
const firstInt = ref(0);
watch(
  [() => props.modelValue, () => props.multipleList],
  ([val, multipleList]) => {
    if (val && firstInt.value == 0) {
      if (typeof val == 'string') {
        if (props.srcDataType == 'sys_user') echoByStaffId(val);
        if (props.srcDataType == 'szxx') echoByTeacherId(val);
      }
      firstInt.value = firstInt.value + 1;
    }
    if (Array.isArray(multipleList) && multipleList.length > 0 && props.multiple) {
      options.value = [...multipleList];
    }
  },
  { immediate: true, deep: true },
);
onUnmounted(() => {
  firstInt.value = 0;
});
function changeVal(val) {
  if (Array.isArray(props.modelValue)) {
    emit('change', options.value.filter((f) => val.includes(f[props.value])) || []); // change事件抛出选中的对象信息
  } else {
    emit('change', options.value.find((f) => f[props.value] === val) || {}); // change事件抛出选中的对象信息
  }
}

function echoByTeacherId(id) {
  remoteLoading.value = true;
  listTeacher({
    pageSize: 2000,
    pageNum: 1,
    id,
  })
    .then((res) => {
      remoteLoading.value = false;
      if (res.code === 200) {
        options.value = (res.rows || []).map((m) => ({
          ...m,
          showName: `${m.name}` + (m.employeeId ? `(${m.employeeId})` : ''),
          userId: m.id, // 师资表id
        }));
      }
    })
    .catch(() => {
      remoteLoading.value = false;
      options.value = [];
    });
}

function echoByStaffId(id) {
  if (!id) {
    return;
  }
  remoteLoading.value = true;
  findStaffQuery(null, id, props.status)
    .then((res) => {
      remoteLoading.value = false;
      if (res.code === 200) {
        options.value = (res.data.rows || []).map((m) => ({
          ...m,
          showName: `${m.name}` + (m.employeeNo ? `(${m.employeeNo})` : ''),
        }));
      }
    })
    .catch(() => {
      remoteLoading.value = false;
      options.value = [];
    });
}
</script>

<style scoped lang="scss"></style>
